import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { WebSocketSubject, webSocket } from "rxjs/webSocket";
import { Store } from "@ngrx/store";
import { DynamicDialogRef } from "primeng/dynamicdialog";

import { signDocumentNcaSuccess } from "@app/user/documents/documents-store/documents.actions";
import { PendingComponent } from "@shared/pending/pending.component";
import { NcaBindPopupComponent } from "@shared/shared-popups/nca-bind-popup/nca-bind-popup.component";
import { NCASignResponseDto, nCASign, testSerts } from "@helper/abstraction/nca-sign";
import { DocumentType } from "@helper/abstraction/documents";
import { OverlayService } from "./overlay.service";
import { TranslationService } from "./translation.service";

export type NcaActionType = "BIND" | "SIGN";

export interface NcaActionDto {
	type?: NcaActionType;
	draftType?: any;
	document?: any;
	bindData?: {
		serialNumber?: string;
		xmlBase64?: string;
	}
};

@Injectable({
	providedIn: "root"
})
export class NcaSignService {
	public ref?: DynamicDialogRef;
	public unsubscribe$$: Subject<void> = new Subject<void>();
	public res$$: Subject<any> = new Subject<any>();

	private wssUrl = "wss://127.0.0.1:13579/";
	private wsSubject$$?: WebSocketSubject<any>;

	constructor(
		private overlayService: OverlayService,
		private translationService: TranslationService,
		private store: Store,
	) { }

	public openLoadingPopup(checkVersion: boolean, description = "CHECK_VERSION"): void {
		if (checkVersion) {
			this.overlayService.ref = this.overlayService.dialog?.open(PendingComponent, {
				data: { description },
				showHeader: false,
				width: '100%',
				height: '100%'
			});
		} else {
			if (this.overlayService.ref || this.overlayService.dialog) this.overlayService.ref?.close();
		}
	}

	public nCASign(xmlBase64: any, id: any, documentType: DocumentType, method: string): void {
		this.openLoadingPopup(true, "SIGN");
		this.wsSubject$$ = webSocket(
				{
					url: this.wssUrl,
					openObserver: {
						next: () => {
							const signData = { ...nCASign, args: { ...nCASign.args, data: xmlBase64 } };
							this.wsSubject$$?.next(signData);
						}
					},
					closeObserver: {
						next: () => {
							this.openLoadingPopup(false);
						}
					}
				}
			);
		this.wsSubject$$.subscribe(
			(res: NCASignResponseDto) => {
				if (res?.body && !Object.keys(res?.body!)?.length) {
					this.openLoadingPopup(false);
				}
				if (res.body?.result?.length) {
					this.openLoadingPopup(false);
					const data = res.body?.result[0]
									.replace(/-----BEGIN CMS-----/g, "")
									.replace(/-----END CMS-----/g, "")
									.replace(/\r\n/g, "")
									.replace(/\n/g, "")
									.trim();
					
					this.store.dispatch(signDocumentNcaSuccess(data, id, documentType, method));
				}
			},
			(error) => {
				this.overlayService?.showNotification$(this.translationService.getTranslation("unavailableNcaLayer"), "error", undefined, undefined, true);
			}
		);
	}

	public nCASignObs(ncaData: NcaActionDto): Observable<any> {
		return webSocket(
				{
					url: this.wssUrl,
					openObserver: {
						next: () => {
							if (ncaData.type === "BIND") {
								this.handleBindData();
							}
							if (ncaData.type === "SIGN") {
								const signData = { ...nCASign, args: { ...nCASign.args, data: ncaData.bindData?.xmlBase64 } };
								this.wsSubject$$?.next(signData);
							}
						}
					},
					closeObserver: {
						next: () => {
							this.openLoadingPopup(false);
							this.overlayService?.showNotification$(this.translationService.getTranslation("unavailableNcaProxy"), "error", undefined, undefined, true);
						}
					}
				}
			).asObservable();
	}

	public handleBindData(): void {
		this.ref = this.overlayService.dialog.open(NcaBindPopupComponent, {});
		this.ref.onClose.pipe(takeUntil(this.unsubscribe$$)).subscribe(result => {
			if (!result) {
				this.openLoadingPopup(false);
			return null;
			}
			this.wsSubject$$?.next(this.nCABind({ bindData: { serialNumber: result } }));
		});
	}

	public setTestSertificates(ncaData: NcaActionDto): void {
		nCASign.args.signerParams.chain = testSerts;
		nCASign.args.data = ncaData.type!;
	}

	private nCABind(ncaData: NcaActionDto): NcaActionDto {
		const signData = nCASign;
		signData.args.signerParams.serialNumber = ncaData.bindData?.serialNumber!;
		return signData as NcaActionDto;
	}
}
